'use client';

import { useCallback, useMemo } from 'react';
import { useQueryState } from 'nuqs';

/** URL search param holding the Code Review repository filter. */
export const CODE_REVIEW_REPO_PARAM = 'repos';

function parseParam(raw: string | null): string[] {
  if (!raw) return [];
  const names = raw
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean);
  return Array.from(new Set(names));
}

/**
 * Code Review repository filter, stored in the URL (`?repos=a,b`).
 *
 * The URL is the single source of truth rather than a store, matching how this
 * page already keeps its active tab (`?section=`) — so a filtered view is
 * shareable, survives refresh, and the Overview and Review Analytics tabs cannot
 * drift apart while both read the same param.
 *
 * `available` prunes the selection to repos that still exist: a bookmarked URL
 * naming a repo the org no longer has would otherwise send a name the API
 * rejects, blanking the page. Pass it once the repo list has loaded; pass
 * undefined while loading so the raw selection is preserved.
 *
 * A URL naming every available repo is collapsed to the empty form, because it
 * means the same thing and produces the same request. Canonicalizing here rather
 * than in the widget keeps one representation of "all repositories", so callers
 * can test `selected.length === 0` without a second definition drifting from it.
 *
 * Returns:
 *   selected — repo names to send to the API, canonicalized ([] means all repos).
 *   rawSelected — the unpruned URL value, for detecting dropped repos.
 *   droppedRepos — selected names absent from `available`.
 *   allSelectionsStale — the URL named repos and none of them survived pruning.
 *     Distinct from `selected` being empty, which is also true when the user
 *     genuinely means all repositories or named every one of them.
 *   setSelected — writes the selection back to the URL (empty clears the param).
 */
export function useCodeReviewRepoFilter(available?: string[]) {
  const [raw, setRaw] = useQueryState(CODE_REVIEW_REPO_PARAM);

  const rawSelected = useMemo(() => parseParam(raw), [raw]);

  const { selected, droppedRepos, allSelectionsStale } = useMemo(() => {
    if (!available) {
      return {
        selected: rawSelected,
        droppedRepos: [],
        allSelectionsStale: false,
      };
    }
    const allow = new Set(available);
    const kept = rawSelected.filter((r) => allow.has(r));
    const dropped = rawSelected.filter((r) => !allow.has(r));
    const isEveryRepo = available.length > 0 && kept.length === available.length;
    return {
      selected: isEveryRepo ? [] : kept,
      droppedRepos: dropped,
      // Only stale when the link asked for something and nothing usable remained.
      // Deriving this from `selected.length === 0` would misfire on a link naming
      // every repo plus one that has since disappeared.
      allSelectionsStale: rawSelected.length > 0 && kept.length === 0,
    };
  }, [rawSelected, available]);

  const setSelected = useCallback(
    (next: string[]) => {
      const cleaned = Array.from(
        new Set(next.map((r) => r.trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b));
      // null removes the param entirely, keeping the all-repos URL clean.
      setRaw(cleaned.length ? cleaned.join(',') : null);
    },
    [setRaw]
  );

  return {
    selected,
    rawSelected,
    droppedRepos,
    allSelectionsStale,
    setSelected,
  };
}
